import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, Image, File, Link, FileText, Loader2 } from 'lucide-react';
import { useAskSwirlStore } from '../../stores/askSwirlStore';
import { useAuth } from '../../contexts/AuthContext';
import { uploadFiles, validateFile, validateFiles } from '../../services/upload.service';
import topicService from '../../services/topicService';
import contentService from '../../services/contentService';

/**
 * Upload Modal
 * Supports image upload, file upload, paste text, paste URL, drag & drop
 */
export default function UploadModal() {
  const { isUploadModalOpen, closeUploadModal, addChip } = useAskSwirlStore();
  const { user } = useAuth();
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('image'); // 'image' | 'file' | 'text' | 'url'
  const [textContent, setTextContent] = useState('');
  const [urlContent, setUrlContent] = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [collectionId, setCollectionId] = useState(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Load default collection ("All") on mount
  useEffect(() => {
    const loadDefaultCollection = async () => {
      if (!user?.id) return;
      try {
        await topicService.ensureAllCollection(user.id);
        const { data: collections } = await topicService.getUserTopics(user.id);
        const allCollection = collections?.find(c => c.name === 'All');
        if (allCollection) {
          setCollectionId(allCollection.id);
        }
      } catch (error) {
        console.error('Error loading default collection:', error);
      }
    };
    if (isUploadModalOpen) {
      loadDefaultCollection();
    }
  }, [isUploadModalOpen, user?.id]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await handleFileUpload(files[0]);
    }
  }, []);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      await handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    if (!user?.id) {
      setError('Please log in to upload files');
      return;
    }

    if (!collectionId) {
      setError('Please wait for collections to load');
      return;
    }

    // Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(file);
      }

      // Upload file using upload service
      const result = await uploadFiles([file], '', collectionId);

      if (result && result.items && result.items.length > 0) {
        const uploadedItem = result.items[0];
        
        // Create chip from uploaded item
        const chip = {
          id: uploadedItem.id || crypto.randomUUID(),
          type: 'item',
          label: uploadedItem.title || file.name,
          thumbnail: uploadedItem.thumbnail_url || preview || null,
        };

        addChip(chip);

        // Reset state
        setPreview(null);
        setUploading(false);
        setError(null);
        closeUploadModal();
      } else {
        throw new Error('Upload completed but no item was returned');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.message || 'Upload failed. Please try again.');
      setUploading(false);
    }
  };

  const handleTextSubmit = async () => {
    if (!textContent.trim() || !user?.id) {
      setError('Please enter some text');
      return;
    }

    if (!collectionId) {
      setError('Please wait for collections to load');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Create a text file from the content
      const blob = new Blob([textContent], { type: 'text/plain' });
      const file = new File([blob], 'note.txt', { type: 'text/plain' });

      // Upload as file
      const result = await uploadFiles([file], textContent, collectionId);

      if (result && result.items && result.items.length > 0) {
        const uploadedItem = result.items[0];
        const chip = {
          id: uploadedItem.id || crypto.randomUUID(),
          type: 'item',
          label: uploadedItem.title || textContent.slice(0, 50) + (textContent.length > 50 ? '...' : ''),
          thumbnail: null,
        };

        addChip(chip);
        setTextContent('');
        setUploading(false);
        setError(null);
        closeUploadModal();
      } else {
        throw new Error('Upload completed but no item was returned');
      }
    } catch (error) {
      console.error('Text upload error:', error);
      setError(error.message || 'Failed to save text. Please try again.');
      setUploading(false);
    }
  };

  const handleUrlSubmit = async () => {
    if (!urlContent.trim() || !user?.id) {
      setError('Please enter a URL');
      return;
    }

    if (!collectionId) {
      setError('Please wait for collections to load');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Use contentService to save URL
      const contentData = {
        user_id: user.id,
        topic_id: collectionId,
        url: urlContent,
        title: urlContent,
        description: `URL saved from Ask Swirl`,
        content_type: 'post',
        channel: (() => {
          // Use static method from ContentService class
          const ContentServiceClass = contentService.constructor;
          return ContentServiceClass.detectChannel ? ContentServiceClass.detectChannel(urlContent) : 'Web';
        })(),
      };

      const { data, error: saveError } = await contentService.addContent(contentData);

      if (saveError) {
        throw new Error(saveError.message || saveError);
      }

      if (data) {
        const chip = {
          id: data.id || crypto.randomUUID(),
          type: 'item',
          label: data.title || urlContent,
          thumbnail: data.thumbnail_url || null,
        };

        addChip(chip);
        setUrlContent('');
        setUploading(false);
        setError(null);
        closeUploadModal();
      } else {
        throw new Error('URL saved but no item was returned');
      }
    } catch (error) {
      console.error('URL upload error:', error);
      setError(error.message || 'Failed to save URL. Please try again.');
      setUploading(false);
    }
  };

  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        await handleFileUpload(file);
        break;
      } else if (items[i].type === 'text/plain') {
        items[i].getAsString((text) => {
          if (text.startsWith('http://') || text.startsWith('https://')) {
            setUrlContent(text);
            setUploadType('url');
          } else {
            setTextContent(text);
            setUploadType('text');
          }
        });
      }
    }
  }, []);

  useEffect(() => {
    if (isUploadModalOpen) {
      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }
  }, [isUploadModalOpen, handlePaste]);

  if (!isUploadModalOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeUploadModal}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-md bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-neutral-200/50 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-200/50">
            <h3 className="text-lg font-semibold text-neutral-900">Upload Content</h3>
            <button
              onClick={closeUploadModal}
              className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
            >
              <X size={18} className="text-neutral-600" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-neutral-200/50">
            {[
              { id: 'image', label: 'Image', icon: Image },
              { id: 'file', label: 'File', icon: File },
              { id: 'text', label: 'Text', icon: FileText },
              { id: 'url', label: 'URL', icon: Link },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setUploadType(id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  uploadType === id
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
            {uploadType === 'image' || uploadType === 'file' ? (
              <div
                ref={dropZoneRef}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  dragActive
                    ? 'border-purple-500 bg-purple-50/50'
                    : 'border-neutral-300 hover:border-neutral-400'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={uploadType === 'image' ? 'image/*' : '*'}
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload size={48} className="mx-auto mb-4 text-neutral-400" />
                <p className="text-sm text-neutral-600 mb-2">
                  Drag and drop or{' '}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="text-purple-600 hover:underline"
                  >
                    browse files
                  </button>
                </p>
                <p className="text-xs text-neutral-500">
                  {uploadType === 'image' ? 'PNG, JPG, GIF up to 10MB' : 'Any file type'}
                </p>
                {preview && (
                  <div className="mt-4">
                    <img
                      src={preview}
                      alt="Preview"
                      className="max-w-full h-32 object-contain mx-auto rounded-lg"
                    />
                  </div>
                )}
              </div>
            ) : uploadType === 'text' ? (
              <div>
                <textarea
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  placeholder="Paste or type your text here..."
                  className="w-full h-48 p-4 border border-neutral-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  onClick={handleTextSubmit}
                  disabled={!textContent.trim() || uploading}
                  className="mt-4 w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      Uploading...
                    </span>
                  ) : (
                    'Upload Text'
                  )}
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="url"
                  value={urlContent}
                  onChange={(e) => setUrlContent(e.target.value)}
                  placeholder="Paste or enter URL..."
                  className="w-full p-4 border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  onClick={handleUrlSubmit}
                  disabled={!urlContent.trim() || uploading}
                  className="mt-4 w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      Uploading...
                    </span>
                  ) : (
                    'Save URL'
                  )}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

